<script lang="ts">
  import { run, preventDefault } from "svelte/legacy";

  import usePurchaseFlow from "../../providers/usePurchaseFlow";
  import type {
    Dates,
    Facility,
    FacilityInformation,
    FacilityValidation,
    Invoice,
    Person,
    PersonValidation,
    TPinResult,
    TPropertiesResponse,
  } from "../../helpers/types";

  import { useQuery, useQueryClient } from "@sveltestack/svelte-query";
  import Card from "../../components/atoms/Card.svelte";
  import SocialSecurity from "../../components/molecules/SocialSecurity.svelte";
  import { Products, useGetProperties } from "../../helpers/api";
  import { personValidator, validateFacility } from "../../helpers/validators";
  import InputDelivery from "../../components/molecules/InputDelivery.svelte";
  import InvoiceMethod from "../../components/molecules/InvoiceMethod.svelte";
  import Button from "../../components/atoms/Button.svelte";
  import Checkbox from "../../components/atoms/Checkbox.svelte";
  import Heading from "../../components/atoms/Heading.svelte";
  import Text from "../../components/atoms/Text.svelte";
  import FacilityForm from "../../components/molecules/FacilityForm.svelte";
  import DatePicker from "../../components/atoms/DatePicker.svelte";
  import DateInformationText from "../../components/atoms/DateInformationText.svelte";
  import { useRendered } from "../../helpers/hooks";
  import { onDestroy, onMount } from "svelte";
  import Loader from "../../components/atoms/Loader.svelte";
  import { getFirstPossibleDate } from "../../helpers/helperMethods";

  const { state: store, send } = usePurchaseFlow();
  const zipcode = $store.context.facilityInformation.zipcode;
  const client = useQueryClient();
  let { rendered } = useRendered();

  let { lastpossibledate } = $props();
  const lastPossibleDate = new Date(lastpossibledate);

  let person = $state({ ...$store.context.person });
  let addressValid = $state(false);
  let selectedInvoice = $state({ ...$store.context.invoice });
  let isInvoiceSelected = $state($store.context.invoice.value ? true : false);
  let facilityInformation = $state({ ...$store.context.facilityInformation });
  let facility = $state({ ...$store.context.facility });
  if (typeof facility?.powerOfAttorney == "undefined") facility.powerOfAttorney = false;
  let personValidation: PersonValidation = $state({ valid: false, errors: {} });
  let facilityValidator: FacilityValidation = $state({ valid: false, errors: {} });
  let useSeperateAddress = $state(false);
  if (person.address && person.address != facilityInformation.address) useSeperateAddress = true;
  let validSocialSecurity = $state(false);
  let invoiceMethodValid = $state(false);
  let billingEmail = $state(
    $store.context.invoice?.label === "E-postadress" ? $store.context.invoice?.value : ""
  );
  let hasPressedContinue = $state(false);
  let hasFetchedPersonAutomatic = $state($store.context.hasFetchedPersonAutomatic);

  const startDate = $store.context.startDate;

  const mustUseFacilityId =
    typeof facility.preSetFacilityId === "string" && typeof facility.preSetAreaId === "string";

  if (mustUseFacilityId && !facility.facilityId && !facility.areaId) {
    facility.facilityId = facility.preSetFacilityId;
    facility.areaId = facility.preSetAreaId;
  }

  const params = {
    code: $store.context.campaign,
  };

  let date = $state(startDate ? startDate : new Date());
  let firstPossible: Date = $state(new Date());
  let firstPossibleInDays: number = $state(21);
  const requiresLimePurchase = $store.context.requiresLimePurchase;

  const setFirstPossibleData = (properties: TPropertiesResponse) => {
    firstPossibleInDays = properties.result.signDays;
    firstPossible = getFirstPossibleDate(firstPossibleInDays);
  };

  const queryResult = useGetProperties(params, {
    staleTime: 10000,
    refetchOnWindowFocus: false,
    enabled: $rendered === true,
    onSuccess: (data) => {
      setFirstPossibleData(data);
    },
  });

  if (client.getQueryData(["properties", params])) {
    const propertiesResponse = client.getQueryData<TPropertiesResponse>(["properties", params]);
    if (propertiesResponse) {
      setFirstPossibleData(propertiesResponse);
    }
  }

  onMount(() => {
    if (!client.getQueryData(["properties", params])) $queryResult.refetch();
  });

  const onPowerOfAttorney = (): void => {
    facility.powerOfAttorney = true;
    date = firstPossible;
  };

  const clearPersonInfo = () => {
    const socialSecurity = person.socialSecurity;
    send("SET_HAS_FETCHED_PERSON_AUTOMATIC", { hasFetchedPersonAutomatic: false });
    hasFetchedPersonAutomatic = false;
    person = {};
    person = {
      socialSecurity: socialSecurity,
      ...person,
    };
    facilityInformation = {};
  };

  if (!startDate) date.setDate(date.getDate() + 21);

  const updatePerson = (lookup: TPinResult) => {
    billingEmail = "";
    selectedInvoice.value = "";
    isInvoiceSelected = false;
    const socialSecurity = person.socialSecurity;
    person = {};
    if (!mustUseFacilityId) {
      facility = {
        powerOfAttorney: false,
        preSetAreaId: undefined,
        preSetFacilityId: undefined,
        valid: false,
      };
    }
    person.socialSecurity = socialSecurity;

    person = {
      firstname: lookup.firstname,
      lastname: lookup.lastname,
      ...person,
    };

    const residenceType = facilityInformation.residenceType;

    facilityInformation = {};
    facilityInformation = {
      fetchedZipcode: lookup.zipcode,
      zipcode: zipcode,
      city: lookup.city,
      address: lookup.address,
      residenceType: residenceType,
      ...facilityInformation,
    };
  };

  const personQuery = useQuery(
    ["socialSecurity", person.socialSecurity],
    () => Products.pinLookup(person.socialSecurity),
    {
      enabled: false,
      onSuccess: (data) => {
        send("SET_HAS_FETCHED_PERSON_AUTOMATIC", { hasFetchedPersonAutomatic: true });
        hasFetchedPersonAutomatic = true;
        updatePerson(data.result);
      },
      cacheTime: 0,
    }
  );

  const onGetPerson = () => {
    $personQuery.remove();
    $personQuery.refetch();
  };

  const sendInformation = (
    facilityInformation: FacilityInformation,
    facility: Facility,
    person: Person,
    date: Date,
    selectedInvoice: Invoice
  ) => {
    send("SET_FACILITY_INFORMATION", { facilityInformation });
    send("SET_FACILITY", { facility });
    send("SET_PERSON", { person });
    send("SET_START_DATE", { startDate: date });
    send("SET_INVOICE", { invoice: selectedInvoice });
  };

  onDestroy(() => {
    sendInformation(facilityInformation, facility, person, date, selectedInvoice);
  });

  const onClickNext = () => {
    if (!ableToContinue) return;
    if (!useSeperateAddress) {
      person = {
        ...person,
        city: facilityInformation.city,
        zipcode: hasFetchedPersonAutomatic
          ? facilityInformation.fetchedZipcode
          : facilityInformation.zipcode,
        address: facilityInformation.address,
      };
    }

    sendInformation(facilityInformation, facility, person, date, selectedInvoice);
    send("NEXT");
  };

  $effect(() => {
    if (person) {
      personValidation = personValidator(person, !useSeperateAddress);
    }
  });

  $effect(() => {
    facilityValidator = validateFacility(facility);
  });

  let ableToContinue = $derived(
    (date >= firstPossible || facility.powerOfAttorney) &&
      invoiceMethodValid &&
      personValidation.valid &&
      validSocialSecurity &&
      addressValid &&
      facilityValidator.valid
  );
</script>

<form onsubmit={preventDefault(onClickNext)}>
  <Card>
    <SocialSecurity
      disableInputs={requiresLimePurchase}
      isLoading={$personQuery.isLoading}
      onChange={() => {
        if (!$personQuery.isIdle) client.cancelQueries();
      }}
      {onGetPerson}
      bind:socialSecurity={person.socialSecurity}
      bind:valid={validSocialSecurity}
      bind:hasPressedContinue
      error={$personQuery.isError}
    />
    <InputDelivery
      {clearPersonInfo}
      bind:hasFetchedPersonAutomatic
      bind:hasPressedContinue
      disableInputs={requiresLimePurchase}
      bind:person
      bind:information={facilityInformation}
      bind:valid={addressValid}
      bind:useSeperateAddress
      {requiresLimePurchase}
    />
  </Card>
  <InvoiceMethod
    {person}
    bind:valid={invoiceMethodValid}
    bind:selectedInvoice
    billingEmail={billingEmail || person.email}
    bind:personMobile={person.mobile}
    campaign={$store.context.campaign}
    {isInvoiceSelected}
    disabled={!person.valid}
  />
  <Card>
    <Heading class="mb-4">Anläggningsuppgifter</Heading>
    <div class="mb-3">
      {#if !mustUseFacilityId}
        <Checkbox
          type={"radio"}
          bind:group={facility.powerOfAttorney}
          value={false}
          checked={facility.powerOfAttorney == false}
          onClick={() => (date = firstPossible)}
        >
          Jag vill själv fylla i anläggningsuppgifter
        </Checkbox>
      {/if}
      {#if facility.powerOfAttorney === false}
        <FacilityForm
          bind:hasPressedContinue
          class="mt-3 fade-in {mustUseFacilityId ? '' : 'ml-9'}"
          bind:facilityValidator
          bind:facility
        >
          <Text variant="primary">Anläggnings-ID (735999xxxxxxxxxxxx)</Text>
        </FacilityForm>

        <div class="{mustUseFacilityId ? '' : 'ml-9'} mt-5">
          <p class="text-Neutral900 text-H5 leading-5 font-bold mb-2">
            Fyll i startdatum för ditt elavtal
          </p>
          {#if $queryResult.isError}
            <div class="flex space-y-4 flex-col my-4">
              <span class="text-Red900 mx-auto text-H5 leading-5 font-bold error-text block"
                >Lyckades inte hämta aktuella datum</span
              >
              <Button class="mx-auto" type="button" onClick={() => $queryResult.refetch()}
                >Försök igen</Button
              >
            </div>
          {:else if $queryResult.isLoading}
            <div class="flex">
              <div class="mx-auto p-4">
                <Loader size="big" />
              </div>
            </div>
          {:else if firstPossible && $queryResult.isSuccess && firstPossibleInDays}
            <div class="flex space-x-2">
              <DatePicker
                autoFocus={false}
                bind:date
                minDate={firstPossible}
                maxDate={lastPossibleDate}
              />
              <div class="my-auto">
                <DateInformationText days={firstPossibleInDays} />
              </div>
            </div>
          {/if}
        </div>
      {/if}
    </div>
    {#if !mustUseFacilityId}
      <div>
        <Checkbox
          type={"radio"}
          bind:group={facility.powerOfAttorney}
          value={true}
          checked={facility.powerOfAttorney === true}
          onClick={() => onPowerOfAttorney()}
        >
          Låt Skellefteå Kraft sköta bytet
        </Checkbox>
        {#if facility.powerOfAttorney}
          <div class="ml-9 fade-in">
            <Text>
              Jag ger Skellefteå Kraft, eller den Skellefteå Kraft förordnar i sitt ställe fullmakt
              att kontakta min nätägare och nuvarande elleverantör för att komplettera mina
              uppgifter samt säga upp mitt befintliga elavtal till det datum då det löper ut.
              Fullmakten är giltig 3 månader fr o m registeringsdatum och är endast giltig vid ett
              byte.
            </Text>
          </div>
        {/if}
      </div>
    {/if}
  </Card>
  <Button
    disabled={$store.context.purchaseFlowType == "v1" ? !ableToContinue : false}
    animate={false}
    onClick={() => {
      hasPressedContinue = true;
    }}
    class="w-full my-2"
    type="submit">Fortsätt</Button
  >
</form>
